/**
 * Footer Component
 * Page footer with Talk Australia branding, contact info, and legal links.
 *
 * Contact details are pulled from the prospect's assigned account manager.
 * This means each staff member's name, email, and phone appear automatically
 * based on who owns the deal in BOS.
 *
 * Fallback defaults to Amit's details if no account manager is assigned.
 */

import React from 'react';

/**
 * Default account manager (Amit) — used when no AM is assigned to the prospect.
 * Staff members are set via the BOS webhook when pushing a proposal:
 *   proposalData.accountManager = { name, email, phone, title }
 */
const DEFAULT_ACCOUNT_MANAGER = {
  name: 'Amit Sewani',
  email: 'amit@talkaustralia.com.au',
  phone: '02 4056 1446',
  phoneTel: '0240561446',
  title: 'Director',
};

const styles = {
  footer: {
    backgroundColor: '#0A0F1D',
    borderTop: '1px solid #1A2332',
    padding: '48px 20px 32px',
    marginTop: '60px',
  },
  inner: {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '40px',
  },
  brand: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    minWidth: '250px',
  },
  logo: {
    fontSize: '22px',
    fontWeight: '800',
    color: '#FFFFFF',
  },
  logoAccent: {
    color: '#0057FF',
  },
  tagline: {
    fontSize: '14px',
    color: '#8B92A5',
    lineHeight: '1.5',
    maxWidth: '300px',
  },
  columns: {
    display: 'flex',
    gap: '60px',
    flexWrap: 'wrap',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  columnTitle: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#FFFFFF',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '4px',
  },
  link: {
    fontSize: '14px',
    color: '#8B92A5',
    textDecoration: 'none',
    transition: 'color 0.2s ease',
    cursor: 'pointer',
  },
  amBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 14px',
    backgroundColor: 'rgba(0, 87, 255, 0.08)',
    borderRadius: '8px',
    border: '1px solid rgba(0, 87, 255, 0.15)',
    marginTop: '8px',
  },
  amName: {
    fontSize: '13px',
    color: '#FFFFFF',
    fontWeight: '600',
  },
  amTitle: {
    fontSize: '11px',
    color: '#8B92A5',
  },
  divider: {
    width: '100%',
    height: '1px',
    backgroundColor: '#1A2332',
    margin: '24px 0',
  },
  bottom: {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
  },
  copyright: {
    fontSize: '13px',
    color: '#5A6175',
  },
  legal: {
    display: 'flex',
    gap: '20px',
  },
  legalLink: {
    fontSize: '13px',
    color: '#5A6175',
    textDecoration: 'none',
    transition: 'color 0.2s ease',
  },
  securityBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    backgroundColor: 'rgba(0, 212, 170, 0.1)',
    borderRadius: '6px',
    fontSize: '12px',
    color: '#00D4AA',
    fontWeight: '500',
    marginTop: '12px',
  },
};

function Footer({ prospect }) {
  const currentYear = new Date().getFullYear();

  // Pull account manager from prospect data, fall back to Amit
  const am = prospect?.proposalData?.accountManager
    || prospect?.accountManager
    || DEFAULT_ACCOUNT_MANAGER;

  const amPhone = am.phone || DEFAULT_ACCOUNT_MANAGER.phone;
  const amPhoneTel = am.phoneTel || amPhone.replace(/\s/g, '');
  const amEmail = am.email || DEFAULT_ACCOUNT_MANAGER.email;
  const amName = am.name || DEFAULT_ACCOUNT_MANAGER.name;
  const amTitle = am.title || '';

  return (
    <footer style={styles.footer}>
      <div style={styles.inner}>
        <div style={styles.brand}>
          <div style={styles.logo}>
            Talk <span style={styles.logoAccent}>Australia</span>
          </div>
          <p style={styles.tagline}>
            Local. Family Owned. Your One-Stop Shop for IT, Telco &amp; Technology Solutions.
          </p>
          <div style={styles.securityBadge}>
            {'\u{1F512}'} Secured Portal &bull; Australian Data Sovereignty
          </div>
        </div>

        <div style={styles.columns}>
          <div style={styles.column}>
            <span style={styles.columnTitle}>Your Account Manager</span>
            <div style={styles.amBadge}>
              <div>
                <div style={styles.amName}>{amName}</div>
                {amTitle && <div style={styles.amTitle}>{amTitle}</div>}
              </div>
            </div>
            <a href={`tel:${amPhoneTel}`} style={styles.link}>{amPhone}</a>
            <a href={`mailto:${amEmail}`} style={styles.link}>{amEmail}</a>
            <span style={styles.link}>Newcastle, NSW</span>
          </div>
          <div style={styles.column}>
            <span style={styles.columnTitle}>Services</span>
            <a href="https://talkaustralia.com.au/managed-it-services/" style={styles.link}>Managed IT Services</a>
            <a href="https://talkaustralia.com.au/cyber-security-services/" style={styles.link}>Cyber Security</a>
            <a href="https://talkaustralia.com.au/iot/" style={styles.link}>IoT &amp; Asset Tracking</a>
            <a href="https://talkaustralia.com.au/" style={styles.link}>Phones, Internet &amp; Mobiles</a>
          </div>
          <div style={styles.column}>
            <span style={styles.columnTitle}>Company</span>
            <a href="https://talkaustralia.com.au/about/" style={styles.link}>About Us</a>
            <a href="https://talkaustralia.com.au/blog/" style={styles.link}>Blog</a>
            <a href="https://talkaustralia.com.au/contact/" style={styles.link}>Contact</a>
          </div>
        </div>
      </div>

      <div style={styles.divider} />

      <div style={styles.bottom}>
        <span style={styles.copyright}>
          &copy; {currentYear} IT &amp; T Communications Pty Ltd t/a Talk Australia. ABN 81 161 895 272. All rights reserved.
        </span>
        <span style={styles.copyright}>
          This page records when it is opened and which sections are viewed, so your account
          manager can follow up. See our Privacy Policy for details.
        </span>
        <div style={styles.legal}>
          <a href="/privacy" style={styles.legalLink}>Privacy Policy</a>
          <a href="/terms" style={styles.legalLink}>Terms of Service</a>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
